import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Package, PaperPlaneTilt, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      {children}
    </div>
  );
}

const UNITS = ["nos", "mtr", "kg", "ltr", "box", "roll", "set", "cyl", "pkt"];
const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export default function MaterialRequestSheet({ open, onOpenChange, projects, projectId, existing, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    project_id: projectId || "",
    priority: "medium",
    required_by: "",
    notes: "",
    items: [{ description: "", sku: "", unit: "nos", quantity: 1, notes: "" }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        project_id: existing.project_id,
        priority: existing.priority,
        required_by: existing.required_by || "",
        notes: existing.notes || "",
        items: existing.items?.length
          ? existing.items.map((i) => ({ ...i, quantity: Number(i.quantity || 1) }))
          : [{ description: "", sku: "", unit: "nos", quantity: 1, notes: "" }],
      });
    } else {
      setForm({
        project_id: projectId || "",
        priority: "medium",
        required_by: "",
        notes: "",
        items: [{ description: "", sku: "", unit: "nos", quantity: 1, notes: "" }],
      });
    }
  }, [open, existing, projectId]);

  const addLine = () =>
    setForm((f) => ({ ...f, items: [...f.items, { description: "", sku: "", unit: "nos", quantity: 1, notes: "" }] }));
  const removeLine = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateLine = (idx, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)) }));

  const save = async (submitAfter = false) => {
    if (!form.project_id) return toast.error("Pick a project");
    const cleanItems = form.items
      .filter((it) => it.description.trim())
      .map((it) => ({
        description: it.description.trim(),
        sku: (it.sku || "").trim(),
        unit: it.unit || "nos",
        quantity: Math.max(Number(it.quantity) || 0, 0),
        notes: it.notes || "",
      }))
      .filter((it) => it.quantity > 0);
    if (!cleanItems.length) return toast.error("Add at least one item with quantity > 0");

    setSaving(true);
    try {
      const payload = {
        project_id: form.project_id,
        priority: form.priority,
        required_by: form.required_by || null,
        notes: form.notes,
        items: cleanItems,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.put(`/material-requests/${existing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/material-requests", payload);
        saved = data;
      }
      if (submitAfter) {
        await api.post(`/material-requests/${saved.id}/submit`);
        toast.success(`${saved.mr_no} submitted for approval`);
      } else {
        toast.success(`${saved.mr_no} saved as draft`);
      }
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[720px] sm:max-w-2xl lg:w-[min(900px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <Package size={18} className="text-[#EA580C]" /> {isEdit ? `Edit ${existing.mr_no}` : "New Material Request"}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Project" required>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="mr-project"><SelectValue placeholder="Choose project…" /></SelectTrigger>
                <SelectContent>
                  {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="mr-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Required by">
              <Input type="date" value={form.required_by} onChange={(e) => setForm({ ...form, required_by: e.target.value })} data-testid="mr-required-by" />
            </Field>
          </div>

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Line items · {form.items.length}</div>
              <button onClick={addLine} data-testid="mr-add-line" className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1">
                <Plus size={12} /> Add line
              </button>
            </div>
            <Table className="bc-list text-[12.5px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-32">SKU (optional)</TableHead>
                  <TableHead className="w-20">Unit</TableHead>
                  <TableHead className="w-20 text-right">Qty</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.map((it, i) => (
                  <TableRow key={`mr-line-${i}`} data-testid={`mr-line-${i}`}>
                    <TableCell>
                      <MaterialAutocomplete
                        value={it.description}
                        onChange={(v) => updateLine(i, "description", v)}
                        onPick={(m) => {
                          setForm((f) => ({
                            ...f,
                            items: f.items.map((row, idx) =>
                              idx === i
                                ? {
                                    ...row,
                                    description: m.description,
                                    sku: m.sku || row.sku,
                                    unit: m.unit || row.unit,
                                  }
                                : row,
                            ),
                          }));
                        }}
                        placeholder="Search past materials or type new…"
                        testid={`mr-line-desc-${i}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input value={it.sku} onChange={(e) => updateLine(i, "sku", e.target.value)} className="h-7 font-mono text-[11.5px]" />
                    </TableCell>
                    <TableCell>
                      <Select value={it.unit} onValueChange={(v) => updateLine(i, "unit", v)}>
                        <SelectTrigger className="h-7"><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input type="number" value={it.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="h-7 text-right font-mono-num" data-testid={`mr-line-qty-${i}`} />
                    </TableCell>
                    <TableCell>
                      <Input value={it.notes} onChange={(e) => updateLine(i, "notes", e.target.value)} className="h-7 text-[12px]" placeholder="Optional…" />
                    </TableCell>
                    <TableCell>
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Field label="Notes / justification">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mr-notes" />
          </Field>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-sm">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => save(false)}
              disabled={saving}
              data-testid="mr-save-draft"
              className="rounded-sm border-slate-300 hover:border-[#EA580C]"
            >
              <FloppyDisk size={14} className="mr-2" /> Save draft
            </Button>
            <Button
              onClick={() => save(true)}
              disabled={saving}
              data-testid="mr-save-submit"
              className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {saving ? "Saving…" : "Save & submit for approval"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
